import type { Issue, IssueStatus } from '../types/issue';
import { getStageOrder, ISSUE_STATUS_LABELS } from '../types/issue';
import type { EnterpriseAction } from '../services/actionService';
import { hasInvestigationFact, isCauseConfirmed } from './problemCause';

export const RESOLUTION_STAGES: { key: string; label: string; statuses: IssueStatus[] }[] = [
  { key: 'ident', label: 'Identificação', statuses: ['open'] },
  { key: 'analysis', label: 'Investigação', statuses: ['investigation'] },
  { key: 'root', label: 'Causa', statuses: ['root_cause'] },
  { key: 'plan', label: 'Plano', statuses: ['action_plan'] },
  { key: 'exec', label: 'Execução', statuses: ['execution'] },
  { key: 'valid', label: 'Validação', statuses: ['validation'] },
  { key: 'monitor', label: 'Monitoramento', statuses: ['monitoring'] },
  { key: 'close', label: 'Encerramento', statuses: ['closed'] },
];

export const QUICK_RESOLUTION_STAGES: { key: string; label: string; statuses: IssueStatus[] }[] = [
  { key: 'ident', label: 'Identificação', statuses: ['open'] },
  { key: 'plan', label: 'Plano', statuses: ['action_plan'] },
  { key: 'exec', label: 'Execução', statuses: ['execution'] },
  { key: 'valid', label: 'Verificação', statuses: ['validation'] },
  { key: 'close', label: 'Encerramento', statuses: ['closed'] },
];

export function workspaceStages(issue: Issue) {
  return issue.mode === 'quick_correction' ? QUICK_RESOLUTION_STAGES : RESOLUTION_STAGES;
}

/** Registro histórico do dossiê. O visto verde do stepper usa stageIsComplete. */
export function stageHasRecord(issue: Issue, actions: EnterpriseAction[], key: string): boolean {
  switch (key) {
    case 'ident':
      return Boolean(issue.title?.trim());
    case 'analysis':
      return Boolean(issue.root_cause_analysis?.problem_statement?.trim());
    case 'root':
      return Boolean(issue.root_cause_analysis?.root_cause_description?.trim() || issue.root_cause?.trim());
    case 'plan':
      return actions.length > 0 || (issue.linked_actions_count ?? 0) > 0;
    case 'exec':
      return actions.some(action => action.status === 'completed' || action.status === 'in_progress');
    case 'valid':
      return Boolean(issue.validation_config?.result || issue.validation_config?.started_at || (issue.validation_config?.inspections?.length ?? 0) > 0);
    case 'monitor':
      return Boolean(issue.monitoring_config);
    case 'close':
      return issue.status === 'closed';
    default:
      return false;
  }
}

export type StageVisual = 'complete' | 'traversed' | 'current' | 'upcoming';

/** Conclusão visual. Causa só fecha com a confirmação humana da P6. */
export function stageIsComplete(issue: Issue, actions: EnterpriseAction[], key: string): boolean {
  switch (key) {
    case 'ident':
      return Boolean(issue.title?.trim());
    case 'analysis':
      return hasInvestigationFact(issue);
    case 'root':
      return isCauseConfirmed(issue);
    case 'plan':
      return actions.length > 0 || (issue.linked_actions_count ?? 0) > 0;
    case 'exec':
      return actions.some((action) => action.status === 'completed');
    case 'valid':
      return Boolean(
        issue.validation_config?.result
        || issue.validation_config?.started_at
        || (issue.validation_config?.inspections?.length ?? 0) > 0,
      );
    case 'monitor':
      return Boolean(issue.monitoring_config);
    case 'close':
      return issue.status === 'closed';
    default:
      return false;
  }
}

export function stageVisual(
  issue: Issue,
  actions: EnterpriseAction[],
  key: string,
  index: number,
  activeIndex: number,
): StageVisual {
  if (key === 'close' && issue.status === 'closed') return 'complete';
  if (index === activeIndex) return 'current';
  if (activeIndex < 0 || index > activeIndex) return 'upcoming';
  return stageIsComplete(issue, actions, key) ? 'complete' : 'traversed';
}

export function computeIssueProgress(issue: Issue, actions: EnterpriseAction[]): number {
  if (issue.status === 'closed') return 100;

  const stageOrder = getStageOrder(issue.mode);
  const stageIdx = Math.max(0, stageOrder.indexOf(issue.status));
  const stagePct = stageOrder.length > 1
    ? (stageIdx / (stageOrder.length - 1)) * 50
    : 0;

  const completed = actions.filter(a => a.status === 'completed').length;
  const actionPct = actions.length > 0 ? (completed / actions.length) * 50 : 0;

  return Math.min(100, Math.round(stagePct + actionPct));
}

export function getMaturityLevel(progress: number): 0 | 25 | 50 | 75 | 100 {
  if (progress >= 100) return 100;
  if (progress >= 75) return 75;
  if (progress >= 50) return 50;
  if (progress >= 25) return 25;
  return 0;
}

export function getActiveStageIndex(issue: Issue): number {
  const stages = workspaceStages(issue);
  if (issue.status === 'closed') return stages.length - 1;
  return stages.findIndex(stage => stage.statuses.includes(issue.status));
}

/** Etapa do método, sem percentual. A posição no roteiro não é maturidade. */
export function formatIssueStageLabel(issue: Issue): string {
  if (issue.status === 'closed') return 'Encerrado';
  if (issue.status === 'cancelled') return 'Cancelado';
  const stages = workspaceStages(issue);
  const idx = stages.findIndex(stage => stage.statuses.includes(issue.status));
  if (idx < 0) return `Fluxo legado · ${ISSUE_STATUS_LABELS[issue.status] ?? issue.status}`;
  const stage = stages[idx];
  const name = issue.mode === 'quick_correction' && stage?.key === 'valid'
    ? stage.label
    : (ISSUE_STATUS_LABELS[issue.status] ?? stage?.label ?? 'Identificação');
  return `${name} · ${idx + 1} de ${stages.length}`;
}

/** Maturidade só existe quando o caso tem dono, causa registrada e ao menos uma ação. */
export function issueHasResolutionMaturity(issue: Issue): boolean {
  if (issue.status === 'closed' || issue.status === 'cancelled') return false;
  const hasOwner = Boolean(issue.assigned_to);
  const cause = issue.root_cause_analysis?.root_cause_description?.trim() || issue.root_cause?.trim();
  const hasAction = (issue.linked_actions_count ?? 0) > 0;
  return hasOwner && Boolean(cause) && hasAction;
}

/** Progresso estimado só pela etapa MASP (lista de problemas, sem ações carregadas). */
export function computeIssueProgressLight(issue: Issue): number {
  if (issue.status === 'closed') return 100;
  const stages = workspaceStages(issue);
  const activeIdx = getActiveStageIndex(issue);
  if (activeIdx < 0) return 0;
  const total = stages.length - 1;
  return total > 0 ? Math.round((activeIdx / total) * 100) : 0;
}

export function actionCompletionPct(action: EnterpriseAction): number {
  if (action.status === 'completed') return 100;
  if (action.status === 'cancelled') return 0;
  if (action.status === 'in_progress') return 50;
  if (action.status === 'blocked') return 25;
  return 0;
}

export function isActionOverdue(action: EnterpriseAction): boolean {
  if (!action.due_date || action.status === 'completed' || action.status === 'cancelled') return false;
  return new Date(action.due_date).getTime() < Date.now();
}
